/**
 * Global Navigation Component - MEGA REDESIGN
 * Primary links: "Otsin tööd", "Pakun tööd"
 * Dropdowns: "Teenused ▾", "Majutus ▾"
 * CTA: "+ Taotle teenust"
 */
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLanguage, useAuth } from '@/lib/context';
import { Button } from '@/components/ui/button';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { PreferencesSheet } from '@/components/PreferencesSheet';
import { 
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetClose
} from '@/components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { 
  Home, Menu, ChevronDown, LogIn, UserPlus,
  Wrench, Truck, Package, Hotel, Building2, 
  Search, Plus, Briefcase, User, Settings, LogOut,
  MapPin, CircleDot, Cog, Car, Droplet, HardHat, Leaf, 
  Plug, Hammer, Paintbrush, Factory, FileSearch, Globe
} from 'lucide-react';

// DEV: Log active route changes
const useRouteLogger = () => {
  const location = useLocation();
  
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`[NAV] Route changed: ${location.pathname}`);
    }
  }, [location.pathname]);
  
  return location;
};

// i18n labels for navigation
const NAV_LABELS = {
  en: {
    lookingForWork: 'Looking for Work',
    offeringWork: 'Offering Work',
    services: 'Services',
    accommodation: 'Accommodation',
    requestService: 'Request Service',
    login: 'Login',
    register: 'Sign Up',
    dashboard: 'Dashboard',
    settings: 'Settings',
    logout: 'Log out',
    myCV: 'My CV',
    myRequests: 'My Requests',
    postJob: 'Post a Job',
    // Services dropdown
    transport: 'Transport',
    localTransport: 'Local Transport',
    longDistance: 'Long Distance',
    moving: 'Moving',
    delivery: 'Delivery',
    autoRepair: 'Auto Repair',
    tires: 'Tires',
    engine: 'Engine',
    brakes: 'Brakes',
    oilChange: 'Oil Change',
    diagnostics: 'Diagnostics',
    equipment: 'Equipment Rental',
    powerTools: 'Power Tools',
    handTools: 'Hand Tools',
    construction: 'Construction',
    garden: 'Garden',
    vehicles: 'Vehicles',
    b2b: 'B2B Services',
    electricalWork: 'Electrical',
    plumbing: 'Plumbing',
    painting: 'Painting',
    manufacturing: 'Manufacturing',
    viewAll: 'View all',
    // Accommodation dropdown
    findAccommodation: 'Find Accommodation',
    offerAccommodation: 'Offer Accommodation',
    // Mobile
    jobs: 'Jobs',
    more: 'More',
    // Provider Map
    map: 'Map',
    logistics: 'Logistics',
    postLoad: 'Post a load',
    mapVisibility: 'Map Visibility',
    providerDashboard: 'Provider Dashboard'
  },
  et: {
    lookingForWork: 'Otsin tööd',
    offeringWork: 'Pakun tööd',
    services: 'Teenused',
    accommodation: 'Majutus',
    requestService: 'Taotle teenust',
    login: 'Logi sisse',
    register: 'Registreeru',
    dashboard: 'Töölaud',
    settings: 'Seaded',
    logout: 'Logi välja',
    myCV: 'Minu CV',
    myRequests: 'Minu päringud',
    postJob: 'Lisa töökuulutus',
    // Services dropdown
    transport: 'Transport',
    localTransport: 'Kohalik transport',
    longDistance: 'Kaugvedu',
    moving: 'Kolimine',
    delivery: 'Pakivedu',
    autoRepair: 'Autoremont',
    tires: 'Rehvid',
    engine: 'Mootor',
    brakes: 'Pidurid',
    oilChange: 'Õlivahetus',
    diagnostics: 'Diagnostika',
    equipment: 'Seadmete rent',
    powerTools: 'Elektritööriistad',
    handTools: 'Käsitööriistad',
    construction: 'Ehitusseadmed',
    garden: 'Aiaseadmed',
    vehicles: 'Sõidukid',
    b2b: 'Alltöövõtt',
    electricalWork: 'Elektritööd',
    plumbing: 'Torutööd',
    painting: 'Värvimine',
    manufacturing: 'Tootmine',
    viewAll: 'Vaata kõiki',
    // Accommodation dropdown
    findAccommodation: 'Otsi majutust',
    offerAccommodation: 'Paku majutust',
    // Mobile
    jobs: 'Tööd',
    more: 'Veel',
    // Provider Map
    map: 'Kaart',
    logistics: 'Logistika',
    postLoad: 'Postita veos',
    mapVisibility: 'Kaardi nähtavus',
    providerDashboard: 'Teenusepakkuja töölaud'
  },
  fi: {
    lookingForWork: 'Etsin työtä',
    offeringWork: 'Tarjoan työtä',
    services: 'Palvelut',
    accommodation: 'Majoitus',
    requestService: 'Pyydä palvelua',
    login: 'Kirjaudu',
    register: 'Rekisteröidy',
    dashboard: 'Hallintapaneeli',
    settings: 'Asetukset',
    logout: 'Kirjaudu ulos',
    myCV: 'CV',
    postJob: 'Lisää työilmoitus',
    // Services dropdown
    transport: 'Kuljetus',
    localTransport: 'Paikallinen kuljetus',
    longDistance: 'Pitkän matkan',
    moving: 'Muutto',
    delivery: 'Toimitus',
    autoRepair: 'Autokorjaamo',
    tires: 'Renkaat',
    engine: 'Moottori',
    brakes: 'Jarrut',
    oilChange: 'Öljynvaihto',
    diagnostics: 'Diagnostiikka',
    equipment: 'Laitevuokraus',
    powerTools: 'Sähkötyökalut',
    handTools: 'Käsityökalut',
    construction: 'Rakennuslaitteet',
    garden: 'Puutarhalaitteet',
    vehicles: 'Ajoneuvot',
    b2b: 'Alihankinta',
    electricalWork: 'Sähkötyöt',
    plumbing: 'Putkityöt',
    painting: 'Maalaus',
    manufacturing: 'Valmistus',
    viewAll: 'Näytä kaikki',
    // Accommodation dropdown
    findAccommodation: 'Etsi majoitusta',
    offerAccommodation: 'Tarjoa majoitusta',
    // Mobile
    jobs: 'Työt',
    more: 'Lisää',
    // Provider Map (missing, add)
    map: 'Kartta',
    logistics: 'Logistiikka',
    postLoad: 'Julkaise kuorma',
    mapVisibility: 'Kartan näkyvyys',
    providerDashboard: 'Hallintapaneeli'
  },
  ru: {
    lookingForWork: 'Ищу работу',
    offeringWork: 'Предлагаю работу',
    services: 'Услуги',
    accommodation: 'Жильё',
    requestService: 'Заказать услугу',
    login: 'Войти',
    register: 'Регистрация',
    dashboard: 'Панель',
    settings: 'Настройки',
    logout: 'Выйти',
    myCV: 'Моё резюме',
    postJob: 'Разместить вакансию',
    // Services dropdown
    transport: 'Транспорт',
    localTransport: 'Местный транспорт',
    longDistance: 'Дальние перевозки',
    moving: 'Переезд',
    delivery: 'Доставка',
    autoRepair: 'Автосервис',
    tires: 'Шины',
    engine: 'Двигатель',
    brakes: 'Тормоза',
    oilChange: 'Замена масла',
    diagnostics: 'Диагностика',
    equipment: 'Аренда оборудования',
    powerTools: 'Электроинструмент',
    handTools: 'Ручной инструмент',
    construction: 'Строительное',
    garden: 'Садовое',
    vehicles: 'Транспорт',
    b2b: 'Субподряд',
    electricalWork: 'Электрика',
    plumbing: 'Сантехника',
    painting: 'Покраска',
    manufacturing: 'Производство',
    viewAll: 'Показать все',
    // Accommodation dropdown
    findAccommodation: 'Найти жильё',
    offerAccommodation: 'Предложить жильё',
    // Mobile
    jobs: 'Работа',
    more: 'Ещё',
    // Provider Map
    map: 'Карта',
    logistics: 'Логистика',
    postLoad: 'Опубликовать груз',
    mapVisibility: 'Видимость на карте',
    providerDashboard: 'Панель исполнителя'
  },
  nb: {
    lookingForWork: 'Søker arbeid',
    offeringWork: 'Tilbyr arbeid',
    services: 'Tjenester',
    accommodation: 'Overnatting',
    requestService: 'Be om en tjeneste',
    login: 'Logg inn',
    register: 'Registrer deg',
    dashboard: 'Oversikt',
    settings: 'Innstillinger',
    logout: 'Logg ut',
    myCV: 'Min CV',
    myRequests: 'Mine forespørsler',
    postJob: 'Publiser jobb',
    transport: 'Transport',
    localTransport: 'Lokal transport',
    longDistance: 'Langdistanse',
    moving: 'Flytting',
    delivery: 'Levering',
    autoRepair: 'Bilverksted',
    tires: 'Dekk',
    engine: 'Motor',
    brakes: 'Bremser',
    oilChange: 'Oljeskift',
    diagnostics: 'Feilsøking',
    equipment: 'Utstyrsutleie',
    powerTools: 'Elektroverktøy',
    handTools: 'Håndverktøy',
    construction: 'Bygg',
    garden: 'Hage',
    vehicles: 'Kjøretøy',
    b2b: 'B2B-tjenester',
    electricalWork: 'Elektriker',
    plumbing: 'Rørlegger',
    painting: 'Maler',
    manufacturing: 'Produksjon',
    viewAll: 'Se alle',
    findAccommodation: 'Finn overnatting',
    offerAccommodation: 'Tilby overnatting',
    jobs: 'Jobber',
    more: 'Mer',
    map: 'Kart',
    logistics: 'Logistikk',
    postLoad: 'Publiser last',
    mapVisibility: 'Synlighet på kart',
    providerDashboard: 'Leverandørpanel'
  },
  sv: {
    lookingForWork: 'Söker arbete',
    offeringWork: 'Erbjuder arbete',
    services: 'Tjänster',
    accommodation: 'Boende',
    requestService: 'Begär tjänst',
    login: 'Logga in',
    register: 'Skapa konto',
    dashboard: 'Översikt',
    settings: 'Inställningar',
    logout: 'Logga ut',
    myCV: 'Mitt CV',
    myRequests: 'Mina förfrågningar',
    postJob: 'Publicera jobb',
    transport: 'Transport',
    localTransport: 'Lokal transport',
    longDistance: 'Långdistans',
    moving: 'Flytt',
    delivery: 'Leverans',
    autoRepair: 'Bilverkstad',
    tires: 'Däck',
    engine: 'Motor',
    brakes: 'Bromsar',
    oilChange: 'Oljebyte',
    diagnostics: 'Felsökning',
    equipment: 'Utrustningsuthyrning',
    powerTools: 'Elverktyg',
    handTools: 'Handverktyg',
    construction: 'Bygg',
    garden: 'Trädgård',
    vehicles: 'Fordon',
    b2b: 'B2B-tjänster',
    electricalWork: 'Elektriker',
    plumbing: 'Rörmokeri',
    painting: 'Måleri',
    manufacturing: 'Tillverkning',
    viewAll: 'Visa alla',
    findAccommodation: 'Hitta boende',
    offerAccommodation: 'Erbjud boende',
    jobs: 'Jobb',
    more: 'Mer',
    map: 'Karta',
    logistics: 'Logistik',
    postLoad: 'Publicera last',
    mapVisibility: 'Synlighet på karta',
    providerDashboard: 'Leverantörspanel'
  }
};

/**
 * Services Dropdown - Transport, Auto, Equipment, B2B
 * Uses controlled state to ensure proper closing on navigation
 */
const ServicesDropdown = ({ language, l }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  
  const handleNavigate = (path) => {
    setOpen(false);
    navigate(path);
  };
  
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition"
          data-testid="nav-services"
        >
          <Wrench className="h-4 w-4" />
          <span>{l.services}</span>
          <ChevronDown className="h-3.5 w-3.5 ml-0.5 opacity-60" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        {/* Transport */}
        <DropdownMenuLabel className="text-xs text-slate-400 uppercase flex items-center gap-2">
          <Truck className="h-3.5 w-3.5" />
          {l.transport}
        </DropdownMenuLabel>
        <DropdownMenuItem onClick={() => handleNavigate('/transport/local')} className="cursor-pointer" data-testid="nav-services-local">
          {l.localTransport}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleNavigate('/transport/long-distance')} className="cursor-pointer" data-testid="nav-services-longdistance">
          {l.longDistance}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleNavigate('/transport/moving')} className="cursor-pointer" data-testid="nav-services-moving">
          {l.moving}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleNavigate('/transport/delivery')} className="cursor-pointer" data-testid="nav-services-delivery">
          {l.delivery}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleNavigate('/logistics')} className="cursor-pointer" data-testid="nav-services-logistics">
          <Truck className="h-3.5 w-3.5 mr-1.5 opacity-60" />
          {l.logistics || 'Logistics'}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleNavigate('/logistics/post-load')} className="cursor-pointer pl-7 text-slate-600" data-testid="nav-services-post-load">
          <Plus className="h-3.5 w-3.5 mr-1.5 opacity-60" />
          {l.postLoad || 'Postita veos'}
        </DropdownMenuItem>
        
        <DropdownMenuSeparator />
        
        {/* Auto Repair */}
        <DropdownMenuLabel className="text-xs text-slate-400 uppercase flex items-center gap-2">
          <Wrench className="h-3.5 w-3.5" />
          {l.autoRepair}
        </DropdownMenuLabel>
        <DropdownMenuItem onClick={() => handleNavigate('/auto-repair/tires')} className="cursor-pointer" data-testid="nav-services-tires">
          {l.tires}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleNavigate('/auto-repair/engine')} className="cursor-pointer" data-testid="nav-services-engine">
          {l.engine}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleNavigate('/auto-repair/brakes')} className="cursor-pointer" data-testid="nav-services-brakes">
          {l.brakes}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleNavigate('/auto-repair/diagnostics')} className="cursor-pointer" data-testid="nav-services-diagnostics">
          {l.diagnostics}
        </DropdownMenuItem>
        
        <DropdownMenuSeparator />
        
        {/* Equipment */}
        <DropdownMenuLabel className="text-xs text-slate-400 uppercase flex items-center gap-2">
          <Package className="h-3.5 w-3.5" />
          {l.equipment}
        </DropdownMenuLabel>
        <DropdownMenuItem onClick={() => handleNavigate('/equipment/power_tools')} className="cursor-pointer">
          {l.powerTools}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleNavigate('/equipment/construction')} className="cursor-pointer">
          {l.construction}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleNavigate('/equipment/vehicles')} className="cursor-pointer">
          {l.vehicles}
        </DropdownMenuItem>
        
        <DropdownMenuSeparator />
        
        {/* B2B */}
        <DropdownMenuLabel className="text-xs text-slate-400 uppercase flex items-center gap-2">
          <Building2 className="h-3.5 w-3.5" />
          {l.b2b}
        </DropdownMenuLabel>
        <DropdownMenuItem onClick={() => handleNavigate('/alltooovott/construction')} className="cursor-pointer">
          {l.construction}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleNavigate('/alltooovott/electrical')} className="cursor-pointer">
          {l.electricalWork}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleNavigate('/alltooovott/plumbing')} className="cursor-pointer">
          {l.plumbing}
        </DropdownMenuItem>
        
        <DropdownMenuSeparator />
        
        {/* Live Map */}
        <DropdownMenuItem onClick={() => handleNavigate('/map')} className="cursor-pointer flex items-center gap-2 font-medium text-orange-600" data-testid="nav-services-map">
          <MapPin className="h-4 w-4" />
          {language === 'et' ? 'Kaart' : 'Live Map'} →
        </DropdownMenuItem>
        
        <DropdownMenuItem onClick={() => handleNavigate('/booking')} className="cursor-pointer font-medium text-orange-600">
          {l.viewAll} →
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/**
 * Accommodation Dropdown - with controlled state
 */
const AccommodationDropdown = ({ language, l }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  
  const handleNavigate = (path) => {
    setOpen(false);
    navigate(path);
  };
  
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition"
          data-testid="nav-accommodation"
        >
          <Hotel className="h-4 w-4" />
          <span>{l.accommodation}</span>
          <ChevronDown className="h-3.5 w-3.5 ml-0.5 opacity-60" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        <DropdownMenuItem onClick={() => handleNavigate('/majutus/otsi')} className="cursor-pointer flex items-center gap-2" data-testid="nav-accommodation-find">
          <Search className="h-4 w-4 text-slate-400" />
          {l.findAccommodation}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleNavigate('/majutus/paku')} className="cursor-pointer flex items-center gap-2" data-testid="nav-accommodation-offer">
          <Plus className="h-4 w-4 text-slate-400" />
          {l.offerAccommodation}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/**
 * User Dropdown Menu - with controlled state
 */
const UserMenu = ({ user, logout, l }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  
  const handleNavigate = (path) => {
    setOpen(false);
    navigate(path);
  };
  
  const handleLogout = () => {
    setOpen(false);
    logout();
  };
  
  const dashboardPath = user.role === 'company' 
    ? '/company/dashboard' 
    : user.role === 'admin' 
      ? '/admin' 
      : '/worker/dashboard';

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-2">
          <div className="w-7 h-7 rounded-full bg-orange-100 flex items-center justify-center">
            <User className="h-4 w-4 text-orange-600" />
          </div>
          <ChevronDown className="h-4 w-4 text-slate-400" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <div className="px-2 py-1.5">
          <p className="text-sm font-medium">{user.nickname || user.email}</p>
          <p className="text-xs text-muted-foreground capitalize">{user.role}</p>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => handleNavigate(dashboardPath)} className="cursor-pointer">
          <Briefcase className="h-4 w-4 mr-2" />
          {l.dashboard}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleNavigate('/dashboard/requests')} className="cursor-pointer">
          <FileSearch className="h-4 w-4 mr-2" />
          {l.myRequests || 'My Requests'}
        </DropdownMenuItem>
        {user.role === 'worker' && (
          <DropdownMenuItem onClick={() => handleNavigate('/cv')} className="cursor-pointer">
            <User className="h-4 w-4 mr-2" />
            {l.myCV}
          </DropdownMenuItem>
        )}
        {user.role === 'company' && (
          <DropdownMenuItem onClick={() => handleNavigate('/jobs/create')} className="cursor-pointer">
            <Plus className="h-4 w-4 mr-2" />
            {l.postJob}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={() => handleNavigate('/settings')} className="cursor-pointer">
          <Settings className="h-4 w-4 mr-2" />
          {l.settings}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600">
          <LogOut className="h-4 w-4 mr-2" />
          {l.logout}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/**
 * Provider Dropdown Menu - with controlled state
 */
const ProviderMenu = ({ l, onLogout }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  
  const handleNavigate = (path) => {
    setOpen(false);
    navigate(path);
  };
  
  const handleLogout = () => {
    setOpen(false);
    onLogout();
  };
  
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-2">
          <div className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center">
            <User className="h-4 w-4 text-green-600" />
          </div>
          <span className="hidden sm:inline text-sm">Provider</span>
          <ChevronDown className="h-4 w-4 text-slate-400" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>{l.providerDashboard || 'Provider'}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => handleNavigate('/provider/dashboard')} className="cursor-pointer">
          <Briefcase className="h-4 w-4 mr-2" />
          {l.dashboard || 'Dashboard'}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleNavigate('/provider/map-visibility')} className="cursor-pointer">
          <MapPin className="h-4 w-4 mr-2" />
          {l.mapVisibility || 'Map Visibility'}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleNavigate('/map')} className="cursor-pointer">
          <CircleDot className="h-4 w-4 mr-2" />
          {l.map || 'Map'}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => handleNavigate('/settings')} className="cursor-pointer">
          <Settings className="h-4 w-4 mr-2" />
          {l.settings || 'Settings'}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600">
          <LogOut className="h-4 w-4 mr-2" />
          {l.logout || 'Log out'}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/**
 * Mobile Header Language Button
 * Compact inline language switcher in mobile header — always accessible
 * independent of the hamburger menu. Opens a small popover with ET/EN/FI/RU.
 */
const MobileHeaderLanguageButton = () => {
  const { language, setLanguage } = useLanguage();
  const [open, setOpen] = useState(false);
  const containerRef = React.useRef(null);

  const languages = [
    { code: 'et', label: 'Eesti', flag: '🇪🇪' },
    { code: 'en', label: 'English', flag: '🇬🇧' },
    { code: 'fi', label: 'Suomi', flag: '🇫🇮' },
    { code: 'nb', label: 'Norsk', flag: '🇳🇴' },
    { code: 'ru', label: 'Русский', flag: '🇷🇺' }
  ];

  const current = languages.find((lng) => lng.code === language) || languages[1];

  // Close popover when clicking outside
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('touchstart', onDocClick);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('touchstart', onDocClick);
    };
  }, [open]);

  const handleSelect = (code) => {
    setLanguage(code);
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('lang', code);
      window.history.replaceState({}, '', url.toString());
      localStorage.setItem('preferredLanguage', code);
    } catch (_) { /* noop */ }
    setOpen(false);
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center justify-center w-11 h-11 rounded-lg hover:bg-slate-100 active:bg-slate-200 touch-manipulation"
        style={{
          minWidth: '44px',
          minHeight: '44px',
          padding: 0,
          WebkitTapHighlightColor: 'transparent',
          touchAction: 'manipulation'
        }}
        aria-label="Change language"
        aria-expanded={open}
        data-testid="mobile-header-lang-btn"
      >
        <span className="text-lg leading-none" aria-hidden="true">{current.flag}</span>
      </button>
      {open && (
        <div
          className="absolute right-0 top-12 z-50 w-44 bg-white rounded-xl shadow-xl border border-slate-200 p-1.5"
          role="menu"
          data-testid="mobile-header-lang-menu"
        >
          {languages.map((lng) => (
            <button
              key={lng.code}
              type="button"
              onClick={() => handleSelect(lng.code)}
              className={cn(
                "w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium text-left",
                language === lng.code
                  ? "bg-orange-100 text-orange-700"
                  : "text-slate-700 hover:bg-slate-100 active:bg-slate-200"
              )}
              style={{ WebkitTapHighlightColor: 'transparent' }}
              data-testid={`mobile-header-lang-${lng.code}`}
            >
              <span className="text-base" aria-hidden="true">{lng.flag}</span>
              <span>{lng.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Main Global Navigation
 */
export const GlobalNav = () => {
  const { language } = useLanguage();
  const { user, logout } = useAuth();
  const location = useRouteLogger(); // DEV: logs route changes
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Check if provider is logged in
  const isProvider = !!localStorage.getItem('provider_session');
  
  const handleProviderLogout = () => {
    localStorage.removeItem('provider_session');
    window.location.href = '/';
  };

  const l = NAV_LABELS[language] || NAV_LABELS.en;

  return (
    <nav className="sticky top-0 z-50 bg-white border-b shadow-sm">
      <div className="max-w-[1140px] mx-auto px-4 sm:px-6">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-2 text-2xl font-bold text-orange-500 hover:text-orange-600 transition"
            data-testid="nav-logo"
          >
            <Home className="h-5 w-5" />
            KEIKKA
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {/* Primary Links */}
            <Link
              to="/jobs"
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition"
              data-testid="nav-looking-work"
            >
              <Search className="h-4 w-4" />
              <span>{l.lookingForWork}</span>
            </Link>
            
            <Link
              to="/jobs/create"
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition"
              data-testid="nav-offering-work"
            >
              <Briefcase className="h-4 w-4" />
              <span>{l.offeringWork}</span>
            </Link>

            {/* Live Map - Always visible */}
            <Link
              to="/map"
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-orange-600 hover:bg-orange-50 hover:text-orange-700 transition"
              data-testid="nav-map"
            >
              <MapPin className="h-4 w-4" />
              <span>{l.map || 'Kaart'}</span>
            </Link>

            {/* Logistics Hub — top-level entry for B2B carriers/shippers */}
            <Link
              to="/logistics"
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition"
              data-testid="nav-logistics"
            >
              <Truck className="h-4 w-4" />
              <span>{l.logistics || 'Logistics'}</span>
            </Link>

            {/* Dropdowns */}
            <ServicesDropdown language={language} l={l} />
            <AccommodationDropdown language={language} l={l} />
          </div>

          {/* Desktop: Actions */}
          <div className="hidden md:flex items-center gap-2">
            {/* Preferences / Settings (also responds to the global
                `keikka:open-prefs` event fired by LocationDetectedBanner) */}
            <PreferencesSheet
              variant="icon"
              testId="nav-settings-trigger"
              respondToGlobalOpen
            />

            {/* Language Switcher */}
            <LanguageSwitcher />

            {/* Primary CTA */}
            <Link to="/booking">
              <Button size="sm" className="bg-orange-500 hover:bg-orange-600" data-testid="nav-request-service">
                <Plus className="h-4 w-4 mr-1" />
                {l.requestService}
              </Button>
            </Link>

            {/* Auth */}
            {isProvider && (
              <ProviderMenu l={l} onLogout={handleProviderLogout} />
            )}
            {user ? (
              <UserMenu user={user} logout={logout} l={l} />
            ) : !isProvider && (
              <div className="flex items-center gap-2">
                <Link to="/login">
                  <Button variant="ghost" size="sm">
                    <LogIn className="h-4 w-4 mr-1" />
                    {l.login}
                  </Button>
                </Link>
                <Link to="/register">
                  <Button variant="outline" size="sm">
                    <UserPlus className="h-4 w-4 mr-1" />
                    {l.register}
                  </Button>
                </Link>
              </div>
            )}
          </div>

          {/* Mobile: Inline language selector + CTA + Hamburger */}
          <div className="flex lg:hidden items-center gap-1">
            {/* Mobile Language Selector - always accessible in header */}
            <MobileHeaderLanguageButton />

            {/* Preferences / Settings — reachable without opening hamburger */}
            <PreferencesSheet variant="icon" testId="mobile-nav-settings-trigger" />

            {/* + Request Service CTA (single element, no nested <a><button>) */}
            <Link
              to="/booking"
              className="inline-flex items-center justify-center w-11 h-11 rounded-lg bg-orange-500 hover:bg-orange-600 active:bg-orange-700 text-white shadow-sm touch-manipulation"
              style={{
                minWidth: '44px',
                minHeight: '44px',
                WebkitTapHighlightColor: 'transparent'
              }}
              aria-label="Request service"
              data-testid="mobile-request-cta"
            >
              <Plus className="h-5 w-5" />
            </Link>

            {/* Hamburger Menu - Apple guideline: min 44x44px touch target */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <button
                  type="button"
                  className="inline-flex items-center justify-center w-11 h-11 rounded-lg hover:bg-slate-100 active:bg-slate-200 touch-manipulation"
                  style={{ 
                    minWidth: '44px', 
                    minHeight: '44px',
                    padding: 0,
                    WebkitTapHighlightColor: 'transparent',
                    touchAction: 'manipulation'
                  }}
                  aria-label="Open menu"
                  data-testid="mobile-menu-trigger"
                >
                  <Menu className="h-6 w-6 text-slate-700" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[85vw] max-w-sm overflow-y-auto p-0">
                <div className="sticky top-0 z-10 bg-white border-b px-4 py-3">
                  <SheetHeader>
                    <SheetTitle className="flex items-center gap-2 text-orange-500">
                      <Home className="h-5 w-5" />
                      KEIKKA
                    </SheetTitle>
                  </SheetHeader>
                </div>
                <div className="px-4 py-4">
                  <MobileMenu 
                    user={user} 
                    logout={logout}
                    l={l}
                    onClose={() => setMobileMenuOpen(false)}
                  />
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
};

/**
 * Mobile Language Selector - Simple toggle for ET/EN
 */
const MobileLanguageSelector = () => {
  const { language, setLanguage } = useLanguage();
  
  const languages = [
    { code: 'et', label: 'Eesti', flag: '🇪🇪' },
    { code: 'en', label: 'English', flag: '🇬🇧' },
    { code: 'fi', label: 'Suomi', flag: '🇫🇮' },
    { code: 'nb', label: 'Norsk', flag: '🇳🇴' },
    { code: 'ru', label: 'Русский', flag: '🇷🇺' }
  ];
  
  const handleLanguageChange = (code) => {
    setLanguage(code);
    // Update URL param
    const url = new URL(window.location.href);
    url.searchParams.set('lang', code);
    window.history.replaceState({}, '', url.toString());
    // Persist to localStorage
    localStorage.setItem('preferredLanguage', code);
  };
  
  return (
    <div className="space-y-2">
      <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-2">
        <Globe className="h-3.5 w-3.5" />
        {language === 'et' ? 'Keel' : language === 'fi' ? 'Kieli' : language === 'ru' ? 'Язык' : 'Language'}
      </label>
      <div className="grid grid-cols-2 gap-2">
        {languages.map((lang) => (
          <button
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            className={cn(
              "flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition touch-manipulation",
              language === lang.code
                ? "bg-orange-100 text-orange-700 border-2 border-orange-500"
                : "bg-slate-100 text-slate-600 border-2 border-transparent hover:bg-slate-200"
            )}
            style={{ WebkitTapHighlightColor: 'transparent' }}
            data-testid={`lang-${lang.code}`}
          >
            <span className="text-base">{lang.flag}</span>
            <span>{lang.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

/**
 * Mobile Menu Content
 * Links are wrapped in <SheetClose asChild> so Radix reliably closes the sheet
 * BEFORE navigation, preventing ghost pointer-events overlays on iOS Safari.
 */
const MobileMenu = ({ user, logout, l, onClose }) => {
  const location = useLocation();
  const isActive = (path) => location.pathname === path;

  return (
    <div className="space-y-6">
      {/* Language Selector - TOP of menu */}
      <div className="pb-4 border-b">
        <MobileLanguageSelector />
      </div>

      {/* Preferences / Settings row */}
      <div className="pb-4 border-b">
        <PreferencesSheet variant="menu-row" testId="mobile-menu-settings-row" />
      </div>

      {/* Primary Actions */}
      <div className="space-y-2">
        <SheetClose asChild>
          <Link to="/booking" className="block">
            <Button className="w-full bg-orange-500 hover:bg-orange-600">
              <Plus className="h-4 w-4 mr-2" />
              {l.requestService}
            </Button>
          </Link>
        </SheetClose>
        
        {/* Live Map - Prominent CTA */}
        <SheetClose asChild>
          <Link to="/map" className="block">
            <Button variant="outline" className="w-full border-orange-200 text-orange-600 hover:bg-orange-50">
              <MapPin className="h-4 w-4 mr-2" />
              {l.map || 'Kaart'} →
            </Button>
          </Link>
        </SheetClose>
      </div>

      {/* Jobs Section */}
      <div>
        <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
          {l.jobs}
        </h3>
        <div className="space-y-1">
          <SheetClose asChild>
            <Link
              to="/jobs"
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-lg transition",
                isActive('/jobs')
                  ? "bg-orange-100 text-orange-700"
                  : "text-slate-600 hover:bg-slate-100"
              )}
            >
              <Search className="h-5 w-5" />
              <span className="font-medium">{l.lookingForWork}</span>
            </Link>
          </SheetClose>
          <SheetClose asChild>
            <Link
              to="/jobs/create"
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-lg transition",
                isActive('/jobs/create')
                  ? "bg-orange-100 text-orange-700"
                  : "text-slate-600 hover:bg-slate-100"
              )}
            >
              <Briefcase className="h-5 w-5" />
              <span className="font-medium">{l.offeringWork}</span>
            </Link>
          </SheetClose>
          <SheetClose asChild>
            <Link
              to="/logistics"
              data-testid="mobile-nav-logistics"
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-lg transition",
                isActive('/logistics')
                  ? "bg-orange-100 text-orange-700"
                  : "text-slate-600 hover:bg-slate-100"
              )}
            >
              <Truck className="h-5 w-5" />
              <span className="font-medium">{l.logistics || 'Logistics'}</span>
            </Link>
          </SheetClose>
          <SheetClose asChild>
            <Link
              to="/logistics/post-load"
              data-testid="mobile-nav-post-load"
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-lg transition pl-10",
                isActive('/logistics/post-load')
                  ? "bg-orange-100 text-orange-700"
                  : "text-slate-600 hover:bg-slate-100"
              )}
            >
              <Plus className="h-4 w-4" />
              <span className="text-sm">{l.postLoad || 'Post a load'}</span>
            </Link>
          </SheetClose>
        </div>
      </div>

      {/* Services Section */}
      <div>
        <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
          {l.services}
        </h3>
        <div className="space-y-1">
          <SheetClose asChild>
            <Link
              to="/transport"
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-600 hover:bg-slate-100"
            >
              <Truck className="h-5 w-5" />
              <span className="font-medium">{l.transport}</span>
            </Link>
          </SheetClose>
          <SheetClose asChild>
            <Link
              to="/auto"
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-600 hover:bg-slate-100"
            >
              <Wrench className="h-5 w-5" />
              <span className="font-medium">{l.autoRepair}</span>
            </Link>
          </SheetClose>
          <SheetClose asChild>
            <Link
              to="/equipment"
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-600 hover:bg-slate-100"
            >
              <Package className="h-5 w-5" />
              <span className="font-medium">{l.equipment}</span>
            </Link>
          </SheetClose>
          <SheetClose asChild>
            <Link
              to="/alltooovott"
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-600 hover:bg-slate-100"
            >
              <Building2 className="h-5 w-5" />
              <span className="font-medium">{l.b2b}</span>
            </Link>
          </SheetClose>
        </div>
      </div>

      {/* Accommodation Section */}
      <div>
        <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
          {l.accommodation}
        </h3>
        <div className="space-y-1">
          <SheetClose asChild>
            <Link
              to="/majutus/otsi"
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-600 hover:bg-slate-100"
            >
              <Search className="h-5 w-5" />
              <span className="font-medium">{l.findAccommodation}</span>
            </Link>
          </SheetClose>
          <SheetClose asChild>
            <Link
              to="/majutus/paku"
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-600 hover:bg-slate-100"
            >
              <Plus className="h-5 w-5" />
              <span className="font-medium">{l.offerAccommodation}</span>
            </Link>
          </SheetClose>
        </div>
      </div>

      {/* Auth Section */}
      <div className="pt-4 border-t">
        {user ? (
          <div className="space-y-2">
            <div className="px-3 py-2">
              <p className="font-medium">{user.nickname || user.email}</p>
              <p className="text-sm text-muted-foreground capitalize">{user.role}</p>
            </div>
            <SheetClose asChild>
              <Link 
                to={user.role === 'company' ? '/company/dashboard' : user.role === 'admin' ? '/admin' : '/worker/dashboard'}
                className="block"
              >
                <Button variant="outline" className="w-full">
                  <Briefcase className="h-4 w-4 mr-2" />
                  {l.dashboard}
                </Button>
              </Link>
            </SheetClose>
            <SheetClose asChild>
              <Link to="/dashboard/requests" className="block">
                <Button variant="ghost" className="w-full">
                  <FileSearch className="h-4 w-4 mr-2" />
                  {l.myRequests || 'Minu päringud'}
                </Button>
              </Link>
            </SheetClose>
            <Button variant="ghost" className="w-full text-red-600" onClick={() => { logout(); onClose(); }}>
              <LogOut className="h-4 w-4 mr-2" />
              {l.logout}
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            <SheetClose asChild>
              <Link to="/login" className="block">
                <Button variant="outline" className="w-full">
                  <LogIn className="h-4 w-4 mr-2" />
                  {l.login}
                </Button>
              </Link>
            </SheetClose>
            <SheetClose asChild>
              <Link to="/register" className="block">
                <Button className="w-full">
                  <UserPlus className="h-4 w-4 mr-2" />
                  {l.register}
                </Button>
              </Link>
            </SheetClose>
          </div>
        )}
      </div>
    </div>
  );
};

export default GlobalNav;
